import {Component} from 'react';
import languages from '../languages.js';
import './InternalLinkSuggestions.scss';
import IconX from './IconX.js';
import LanguageInfo from './LanguageInfo.js';
import SeoHelper from '../lib/SeoHelper.js';
import Loader from './Loader.js';
import Markup from '../lib/Markup.js';
import IconPadlock from './IconPadlock.js';

class InternalLinkSuggestions extends Component {
	constructor(props) {
		super(props);

		this.state = {
			loading: false,
			error: '',
			language: languages.find((lang) => lang.langCode === this.props.appSettings.language),
			linkSuggestions: [],
		}

		this.handleButtonClick = this.handleButtonClick.bind(this);
		this.handleAddLinkButtonClick = this.handleAddLinkButtonClick.bind(this);
	}

	componentDidUpdate() {
		if (this.props.appSettings.language !== this.state.language.langCode) {
			this.setState({
				language: languages.find((lang) => lang.langCode === this.props.appSettings.language),
			})
		}
	}

	handleAddLinkButtonClick(event) {
		const {word, url} = event.target.dataset;
		this.insertLink(word, url);
	}

	handleButtonClick(event) {
		event.preventDefault();
		this.getLinkSuggestions();
	}

	insertLink(linkedWord, url) {
		const markup = document.createElement('div');
		markup.innerHTML = this.props.content;

		let elements = markup.querySelectorAll('dd, li, p');
		if (!elements) {
			return;
		}
		let done = false;
		let linkAdded = false;
		let punctuation = ['.', ',', ' ', '?', '!', ':', ';', '/'];
		elements.forEach((el) => {
			if (done) {
				return;
			}

			let html = el.innerHTML;
			let htmlLower = html.toLocaleLowerCase();
			let wordStart = -1;

			for (let i = 0; i < punctuation.length; i ++) {
				wordStart = htmlLower.indexOf(linkedWord.toLocaleLowerCase() + punctuation[i]);
				if (wordStart !== -1) {
					break;
				}
			}

			if (wordStart === -1) {
				return;
			}
			let wordEnd = wordStart + linkedWord.length;
			let linkEnd = htmlLower.indexOf('</a>', wordEnd);
			let isLinked = linkEnd >= 0;
			if (isLinked) {
				let between = htmlLower.substring(wordEnd, linkEnd + wordEnd);
				if (between.indexOf('<a') !== -1) {
					isLinked = false;
				}
			}

			if (isLinked) {
				done = true;
				return;
			}

			let before = html.substring(0, wordStart);
			let word = html.substring(wordStart, wordEnd);
			let after = html.substring(wordEnd);
			html = `${before}<a href="${url}">${word}</a>${after}`;
			el.innerHTML = html;
			done = true;
			linkAdded = true;
			if (markup.innerHTML !== this.props.content) {
				this.props.contentCallback(Markup.removeEmptyLines(markup.innerHTML));
			}
		});

		if (!linkAdded) {
			alert(`"${linkedWord}" was not found within text - make sure it occurs in paragraphs or list items and is not already linked.`);
		} else {
			alert(`Link added.`);
		}
	}

	async getLinkSuggestions() {
		const {isPro} = this.props;
		if (!isPro) {
			alert('This feature is available in paid version');
			return;
		}
		if (!this.props.siteUrl) {
			this.setState({
				error: 'No site URL provided.',
			});
			return;
		}

		let isUrlValid = true;
		try {
			new URL(this.props.siteUrl);
		} catch(err) {
			isUrlValid = false;
		}
		if (!isUrlValid) {
			this.setState({
				error: 'Site URL is invalid.'
			});
			return;
		}

		this.setState({
			loading: 'Fetching link suggestions. This may take a while...',
			error: '',
		});
		let searchVol = [];
		let results = [];

		let keywords = await this.fetchKeywords(this.props.content) || [];
		if (keywords.length && this.props.focusKeyword) {
			keywords = keywords.filter((keyword) => keyword.toLowerCase() !== this.props.focusKeyword.toLowerCase());
		}

		if (!keywords.length) {
			this.setState({
				loading: false,
				error: keywords && keywords.error ? keywords.error.message : 'No keywords found',
			});
			return;
		}

		const searchVolData = await this.fetchSearchVolume(keywords, this.state.language.langCode);
		if (searchVolData && searchVolData.status !== 'ok') {
			this.setState({
				loading: false,
				error: searchVolData.message,
			});
		} else if (searchVolData) {
			searchVol = searchVolData.data.sort((a, b) => {
				return (a.searchVolume - b.searchVolume) * -1;
			});
			searchVol = searchVol.filter((item) => !!item.searchVolume);
			searchVol = searchVol.splice(0, 30);
		}
		if (!searchVol.length) {
			this.setState({
				loading: false,
				error: 'Could not find Search Volume for keywords. Try again.'
			});
			return;
		}
		results = searchVol;
		this.setState({
			linkSuggestions: results
		});

		if (this.props.siteUrl && searchVol) {
			let requestCount = keywords.length;

			keywords.forEach((keyword) => {
				const data = new FormData();
				data.append('keywords', JSON.stringify([keyword]));
				data.append('website', this.props.siteUrl);
				data.append('language', this.state.language.id);
				fetch('/tools/api/get-link-suggestions.php', {
					method: 'POST',
					body: data
				})
				.then((response) => {
					if (response.status < 400) {
						return response.json();
					}
					return {
						status: 'error',
					}
				})
				.then((respData) => {
					if (respData.status === 'ok' && respData.data.length) {
						let index = results.findIndex((item) => item.keyword === respData.data[0].keyword);
						if (index > -1) {
							results[index].url = respData.data[0].url;
						}
						this.setState({
							linkSuggestions: results
						})
					}
					requestCount --;
					if (!requestCount) {
						this.setState({
							loading: false,
						});
					}
				})
				.catch((error) => console.error(error));
			})
		}
	}

	fetchKeywords(text) {
		const markup = document.createElement('div');
		markup.innerHTML = text;

		return new Promise((resolve) => {
			const data = new FormData();
			data.append('text', markup.textContent);

			// fetch('/tools/api/get-keywords-from-text.php', {
			fetch('ajax/keywords-from-text.php', {
				method: 'POST',
				body: data
			})
			.then((response) => {
				if (response.status < 400) {
					return response.json();
				}
				resolve({});
			})
			.then((respData) => {
				resolve(respData);
			})
			.catch((error) => {
				console.error(error);
				resolve({})
			})
		});
	}

	fetchSearchVolume(keywords, language) {
		return new Promise((resolve) => {
			const data = new FormData();
			data.append('phrases', JSON.stringify(keywords));
			data.append('language', language);

			fetch('/tools/api/get-search-volume.php', {
				method: 'POST',
				body: data
			})
			.then((response) => {
				if (response.status < 400) {
					return response.json();
				}
				resolve({
					status: 'error',
				});
			})
			.then((respData) => {
				resolve(respData);
			})
			.catch((error) => {
				console.error(error);
				resolve({
					status: 'error',
				})
			});
		});
	}

	render () {
		const {isPro} = this.props;
		const loadingInfo = this.state.loading
			? (<p className="waiting">{this.state.loading}</p>)
			: isPro
				? (<p className="main-button"><button className="button primary" id="get-suggestions" onClick={this.handleButtonClick}>Generate link suggestions</button></p>)
				: (<p className="main-button"><button className="button primary has-tooltip" id="get-suggestions" onClick={this.handleButtonClick}>Generate link suggestions<span className="tooltip"><IconPadlock />Available in Advanced plan</span></button></p>);
		const error = this.state.error ? (<p className="error"><IconX /> {this.state.error}</p>) : null;
		let {linkSuggestions} = this.state;

		let suggestions = '';
		if (!error && linkSuggestions.length) {
			let suggestionData = linkSuggestions.map((item, index) => {
				let key = 'link-suggestion-' + index;
				const urlContent = item.url
				? (
					<>
						<a href={item.url} target="_blank" className="suggested-link" rel="noreferrer">{item.url}</a><br />
						<button className="button secondary data-action-button" data-word={item.keyword} data-url={item.url} onClick={this.handleAddLinkButtonClick}>Add link to text</button>
					</>
				)
				: this.state.loading ? (<Loader />) : 'N/A';

				return (
					<tr key={key}>
						<td>{item.keyword}</td>
						<td>{item.searchVolume}</td>
						<td>{urlContent}</td>
					</tr>
				)
			});

			suggestions = (
				<div className="table">
					<table>
						<thead>
							<tr>
								<th>Keyword</th>
								<th>Search volume</th>
								<th>Suggested link</th>
							</tr>
						</thead>
						<tbody>
							{suggestionData}
						</tbody>
					</table>
				</div>
			);
		}

		return (
			<>
			<h4>Internal link suggestions</h4>
			<LanguageInfo language={this.state.language} toggleSettingsCallback={this.props.toggleSettingsCallback} />
			{error}
			{loadingInfo}
			{suggestions}
			</>
		)
	}
}

export default InternalLinkSuggestions;